import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CheckCircle, TrendingDown, TrendingUp } from "@mui/icons-material";
import { ProjectListItem } from "../../types/projectTypes";
import { formatDate, formatMoney, formatPercent } from "./projectUtils";

// Status colours (good / warning) and a neutral info blue - always shown together with an icon
// and a text label, never as colour alone.
const HEALTH = {
    good: { color: '#0ca30c', label: 'On track', Icon: CheckCircle },
    behind: { color: '#fab219', label: 'Invoicing behind schedule', Icon: TrendingDown },
    ahead: { color: '#1976d2', label: 'Invoicing ahead of schedule', Icon: TrendingUp },
} as const;

type HealthKey = keyof typeof HEALTH;

// Invoicing more than 20 points behind (or ahead of) the elapsed time is flagged.
export const HEALTH_STYLES = HEALTH;

export const healthOf = (completion: number | null, elapsed: number | null): HealthKey | null => {
    if (completion === null || elapsed === null || completion === undefined || elapsed === undefined) return null;
    const gap = Number(completion) - Number(elapsed);
    return gap < -0.2 ? 'behind' : gap > 0.2 ? 'ahead' : 'good';
};

const clampPercent = (ratio: number | null) => (ratio === null || ratio === undefined ? 0 : Math.min(100, Math.max(0, Number(ratio) * 100)));

type ProjectProgressProps = {
    item: Pick<ProjectListItem, 'completion_ratio' | 'time_elapsed_ratio' | 'invoiced_total' | 'contract_volume' | 'currency'
        | 'effective_start_date' | 'start_is_estimated' | 'project_end_date' | 'status'>;
    // bar = just the bar (headline card); compact = with labels; full = finance tab (taller bar, amounts under it)
    variant?: 'bar' | 'compact' | 'full';
};

// One slim bar: the fill is "% invoiced", the thin vertical marker is "today" (share of the
// project duration already elapsed). The gap between both tells whether invoicing keeps pace.
function ProjectProgress({ item, variant = 'compact' }: ProjectProgressProps) {
    const theme = useTheme();
    const invoiced = item.completion_ratio;
    const elapsed = item.time_elapsed_ratio;
    const health = item.status === 'Ongoing' ? healthOf(invoiced, elapsed) : null;
    const fillColor = health ? HEALTH[health].color : theme.palette.primary.main;
    const height = variant === 'full' ? 12 : variant === 'bar' ? 6 : 8;

    const tooltip = (
        <Box sx={{ padding: 0.5 }}>
            <Typography variant="body2">Invoiced {formatPercent(invoiced, 1)} · {formatMoney(item.invoiced_total, item.currency)} of {formatMoney(item.contract_volume, item.currency)}</Typography>
            <Typography variant="body2">Time elapsed {formatPercent(elapsed, 1)}</Typography>
            <Typography variant="caption">
                {formatDate(item.effective_start_date)}{item.start_is_estimated ? ' (start estimated from first invoice)' : ''} – {formatDate(item.project_end_date)}
            </Typography>
        </Box>
    );

    const bar = (
        <Tooltip title={tooltip} arrow placement="top">
            <Box sx={{ position: 'relative', height, borderRadius: height, backgroundColor: alpha(theme.palette.text.primary, 0.08), cursor: 'default' }}>
                <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: `${clampPercent(invoiced)}%`, borderRadius: height,
                    background: `linear-gradient(90deg, ${alpha(fillColor, 0.7)}, ${fillColor})`, transition: 'width 0.6s ease',
                }} />
                {elapsed !== null && (
                    <Box sx={{
                        position: 'absolute', top: -3, bottom: -3, left: `calc(${clampPercent(elapsed)}% - 1px)`, width: 2, borderRadius: 1,
                        backgroundColor: theme.palette.text.primary, boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                    }} />
                )}
            </Box>
        </Tooltip>
    );

    if (variant === 'bar') return bar;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ marginBottom: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPercent(invoiced)} <Typography component="span" variant="caption" color="text.secondary">invoiced</Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {elapsed === null ? 'no dates' : `${formatPercent(elapsed)} of time elapsed${item.start_is_estimated ? '*' : ''}`}
                </Typography>
            </Stack>
            {bar}
            {(health || variant === 'full') && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginTop: 0.75 }}>
                    {health ? (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            {(() => { const { Icon } = HEALTH[health]; return <Icon sx={{ fontSize: 16, color: HEALTH[health].color }} />; })()}
                            <Typography variant="caption" color="text.secondary">{HEALTH[health].label}</Typography>
                        </Stack>
                    ) : <span />}
                    {variant === 'full' && (
                        <Typography variant="caption" color="text.secondary">
                            {formatMoney(item.invoiced_total, item.currency)} / {formatMoney(item.contract_volume, item.currency)}
                        </Typography>
                    )}
                </Stack>
            )}
        </Box>
    );
}

export default ProjectProgress;
